import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import {
  GET_CART_LIST,
  UPDATE_CART_LIST,
  DELETE_CART_LIST,
  SET_SAVING_INDICATOR,
  RESET_CART_LOADING,
  SAVE_CART_MESSAGES
} from "../constants";
import { normalizeCartValues } from "../../util/cart";
import {
  CLEAR_CART_AND_WISHLIST_GENERAL,
  SET_CART_AND_WISHLIST_GENERAL,
  UPDATE_ITEM_QUANTITY,
  WISHLIST_GENERAL_LOADING
} from "../constants";
import { itemsInCart } from "../../util";

import { Cart, OrderSummary } from "../../services/cartService";

type WishlistProduct = {
  _id: string;
  productId: number;
  size: string;
  sizeType: string | null;
  updatedDate?: string;
};

type MessagesType = {
  code?: string;
  longMessage?: string;
  shortMessage?: string;
  type?: string;
}[];

type InitialState = Partial<{
  cart: Cart;
  messages: MessagesType;
  isSaving: boolean;
  cartAndWishlistGeneralData: {
    cart: { count: number };
    wishList: { count: number; products: WishlistProduct[] };
  };
  wishListAndCartGeneralLoaded: boolean;
  deleteWishListItemLoaded: boolean;
  cartSummary: OrderSummary | null;
  isLoading: boolean;
}>;

const initialState: InitialState = {
  cart: [],
  messages: [],
  isSaving: false,
  cartAndWishlistGeneralData: {
    cart: { count: 0 },
    wishList: { count: 0, products: null }
  },
  wishListAndCartGeneralLoaded: false,
  deleteWishListItemLoaded: false,
  cartSummary: null,
  isLoading: false
};

const getUpdatedGeneralCartCount = (state: InitialState, cart) => ({
  cartAndWishlistGeneralData: {
    ...state.cartAndWishlistGeneralData,
    cart: { ...state.cartAndWishlistGeneralData.cart, count: itemsInCart(cart) }
  }
});

const getCartList = (state: InitialState, { cart, isProductMerged }) => ({
  ...state,
  cart: (cart || []).map(item => {
    const normalizedItem = normalizeCartValues({ item });
    const prevItem = state.cart.find(el => {
      return el._id === get(normalizedItem, "_id");
    });
    const prevValue =
      //@ts-ignore
      prevItem && !isProductMerged ? { prevValue: prevItem.prevValue } : {};

    return {
      ...normalizedItem,
      ...prevValue,
      isProductMerged
    };
  }),
  isLoading: true,
  ...getUpdatedGeneralCartCount(state, cart)
});

const updateCartList = (state: InitialState, { cart }) => ({
  ...state,
  cart: [cart, ...state.cart],
  ...getUpdatedGeneralCartCount(state, [cart, ...state.cart])
});

const deleteCartList = (state: InitialState, { _id }) => {
  const _cart = state.cart.filter(o => o._id !== _id);
  return {
    ...state,
    cart: _cart,
    ...getUpdatedGeneralCartCount(state, _cart)
  };
};

const updateItemQuantity = (state: InitialState, { productId, quantity }) => {
  const clonedCart = cloneDeep(state.cart);
  const updatedItem = clonedCart.find(item => item._id === productId);

  if (updatedItem) {
    updatedItem.quantity = quantity;
  }

  return {
    ...state,
    cart: [...clonedCart]
  };
};

const setSavingIndicator = (state: InitialState, { value }) => ({
  ...state,
  isSaving: value
});

export const wishListGeneralLoading = (state: InitialState) => ({
  ...state,
  wishListAndCartGeneralLoaded: false
});

const setCartAndWishlistGeneralData = (
  state: InitialState,
  { cartAndWishlistGeneralData }
) => ({
  ...state,
  cartAndWishlistGeneralData,
  wishListAndCartGeneralLoaded: true
});

const removeCartAndWishlistGeneral = (state: InitialState) => ({
  ...state,
  cartAndWishlistGeneralData: initialState.cartAndWishlistGeneralData,
  cart: [],
  messages: []
});

const resetCartLoading = (state: InitialState) => ({
  ...state,
  isLoading: false
});

const saveCartMessages = (state: InitialState, { messages }) => ({
  ...state,
  messages
});

const cartReducer = (
  state: InitialState = initialState,
  action
): InitialState => {
  switch (action.type) {
    case GET_CART_LIST:
      return getCartList(state, action);
    case UPDATE_CART_LIST:
      return updateCartList(state, action);
    case DELETE_CART_LIST:
      return deleteCartList(state, action);
    case SET_SAVING_INDICATOR:
      return setSavingIndicator(state, action);
    case SET_CART_AND_WISHLIST_GENERAL:
      return setCartAndWishlistGeneralData(state, action);
    case CLEAR_CART_AND_WISHLIST_GENERAL:
      return removeCartAndWishlistGeneral(state);
    case WISHLIST_GENERAL_LOADING:
      return wishListGeneralLoading(state);
    case RESET_CART_LOADING:
      return resetCartLoading(state);
    case SAVE_CART_MESSAGES:
      return saveCartMessages(state, action);

    default:
      return state;
  }
};

export default cartReducer;
